import React, { useState } from "react";
import { ClipboardList, Loader2, CheckCircle2 } from "lucide-react";
import { clientPortalApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtDate, apiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const DOOR_TYPES = [
  { key: "communal", label: "Communal doors" },
  { key: "apartment", label: "Apartment entrance doors" },
];

function statusTone(s) {
  return { pending: "bg-amber-100 text-amber-700", accepted: "bg-emerald-100 text-emerald-700", declined: "bg-slate-200 text-slate-600" }[s] || "bg-slate-100 text-slate-600";
}

export default function PortalRequestInspection() {
  const own = useAsync(() => clientPortalApi.listOwnInspectionRequests(), []);
  const [propName, setPropName] = useState("");
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [doorCount, setDoorCount] = useState("");
  const [access, setAccess] = useState("");
  const [parking, setParking] = useState("");
  const [notes, setNotes] = useState("");
  const [types, setTypes] = useState(new Set(["communal", "apartment"]));
  const [saving, setSaving] = useState(false);

  const toggleType = (k) => setTypes((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const submit = async () => {
    if (!propName.trim() || !address.trim() || !postcode.trim()) { toast.error("Property name, address and postcode are required."); return; }
    setSaving(true);
    try {
      await clientPortalApi.requestInspection({
        property_name: propName.trim(),
        address: address.trim(),
        postcode: postcode.trim(),
        approx_door_count: doorCount ? parseInt(doorCount, 10) : null,
        notes: notes.trim() || undefined,
        access_details: access.trim() || undefined,
        parking_details: parking.trim() || undefined,
        door_types: Array.from(types),
      });
      toast.success("Request sent — your provider will be in touch");
      setPropName(""); setAddress(""); setPostcode(""); setDoorCount(""); setAccess(""); setParking(""); setNotes(""); setTypes(new Set(["communal", "apartment"]));
      own.reload();
    } catch (e) { toast.error(apiError(e, "Could not send request")); }
    finally { setSaving(false); }
  };

  const ownList = Array.isArray(own.data) ? own.data : [];

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Request an inspection" subtitle="Ask your provider to survey a new building" testId="portal-request-header" />

      <Card className="space-y-4 p-5" data-testid="portal-request-form">
        <div>
          <Label>Property name *</Label>
          <Input value={propName} onChange={(e) => setPropName(e.target.value)} placeholder="e.g. Mulberry Court" className="mt-1" data-testid="req-property" />
        </div>
        <div>
          <Label>Address *</Label>
          <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. 12 Mulberry Ave, York" className="mt-1" data-testid="req-address" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>Postcode *</Label>
            <Input value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} placeholder="YO1 1AA" className="mt-1" data-testid="req-postcode" />
          </div>
          <div>
            <Label>Approx. door count</Label>
            <Input inputMode="numeric" value={doorCount} onChange={(e) => setDoorCount(e.target.value)} placeholder="e.g. 40" className="mt-1" data-testid="req-door-count" />
          </div>
        </div>
        <div>
          <Label className="mb-1.5 block">Door types</Label>
          <div className="flex flex-wrap gap-2">
            {DOOR_TYPES.map((t) => (
              <button key={t.key} type="button" onClick={() => toggleType(t.key)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium ${types.has(t.key) ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}
                data-testid={`req-type-${t.key}`}>{t.label}</button>
            ))}
          </div>
        </div>
        <div>
          <Label>Access details</Label>
          <Textarea value={access} onChange={(e) => setAccess(e.target.value)} placeholder="Key safe codes, concierge hours, etc." className="mt-1" data-testid="req-access" />
        </div>
        <div>
          <Label>Parking details</Label>
          <Input value={parking} onChange={(e) => setParking(e.target.value)} placeholder="On-site / permit / nearest car park" className="mt-1" data-testid="req-parking" />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else the surveyor should know…" className="mt-1" data-testid="req-notes" />
        </div>
        <div className="flex justify-end">
          <Button onClick={submit} disabled={saving} data-testid="req-submit">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardList className="mr-2 h-4 w-4" />} Send request
          </Button>
        </div>
      </Card>

      {ownList.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-2 font-semibold text-slate-800">Your requests</h3>
          <div className="space-y-2">
            {ownList.map((r) => (
              <Card key={r.id} className="flex items-center justify-between gap-3 p-3.5" data-testid={`portal-own-request-${r.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">{r.property_name}</p>
                  <p className="truncate text-xs text-slate-400">{[r.address, r.postcode].filter(Boolean).join(", ")} · {fmtDate(r.created_at)}</p>
                </div>
                <Badge className={statusTone(r.status)}>{r.status === "accepted" ? <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> : null}{r.status}</Badge>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
